"use client";

/**
 * Animated "lineage graph" — a 4D constellation (the calmed Hyperdrive from the
 * design mockups, 2026-07-10). Nodes are blue-noise sampled in a flattened
 * 4-ellipsoid; edges are length-capped nearest neighbours; ~35% of nodes carry
 * short dead-end twigs (leaves). The cloud performs a slow tesseract-style
 * double rotation (X–W and Z–W planes, one inside-out turn per ~90s), is
 * projected 4D→3D→2D with CLAMPED perspective divides (unclamped, a node
 * magnified by w and rotated near the 3D camera walks the 3D denominator
 * through zero — coordinate blowup, rasterizer stall), and hue drifts subtly
 * cyan↔fuchsia with w so the 4th axis is faintly visible.
 *
 * Quieting: a vignette dims the base mesh toward the centre (behind the text);
 * cursor tracing (edges + lit nodes around the pointer) ignores the vignette.
 * Parallax on scroll. pointer-events: none; prefers-reduced-motion renders a
 * single static frame.
 */
import { useEffect, useRef } from "react";

interface CloudNode {
  X: number; // model space (4D)
  Y: number;
  Z: number;
  W: number;
  phase: number;
  r: number;
  accent: boolean;
  leaf: boolean;
}

interface ProjNode {
  x: number;
  y: number;
  r: number;
  accent: boolean;
  scale: number;
  fog: number;
  tint: string | null;
}

const CURSOR_DIST = 190;
const PARALLAX = 0.15;
const HUE_STRENGTH = 0.9; // w-hue drift; the mockup dial's "deep field 9"
const YAW_SPEED = 0.00008;
const XW_SPEED = 0.00023; // one inside-out turn per ~27s (deep field 9)
const ZW_SPEED = 0.00013;
const TRAIL_ALPHA = 0.35; // motion-trail fade; matches body --bg (#05070a)

// Neon-pastel blue (96,165,250) → soft violet (150,120,245) by t (top → bottom).
function mix(t: number): string {
  const c = Math.max(0, Math.min(1, t));
  const r = Math.round(96 + 54 * c);
  const g = Math.round(165 - 45 * c);
  const b = Math.round(250 - 5 * c);
  return `${r},${g},${b}`;
}

// 4D hue: w<0 leans cyan (103,232,249), w>0 leans fuchsia (232,121,249).
function mix4(yT: number, wT: number): string {
  const base = mix(yT).split(",").map(Number);
  const target = wT >= 0 ? [232, 121, 249] : [103, 232, 249];
  const k = Math.min(1, Math.abs(wT)) * HUE_STRENGTH;
  return base.map((v, i) => Math.round(v + (target[i]! - v) * k)).join(",");
}

export function LineageGraph({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const mouse = { x: -9999, y: -9999, active: false };

    let width = 0;
    let height = 0;
    let R = 1;
    let pts: CloudNode[] = [];
    let edges: Array<[number, number]> = [];
    let proj: ProjNode[] = [];

    // Blue-noise (best-candidate) sampling in a flattened 4-ellipsoid,
    // k-nearest edges capped in length, plus dead-end leaf twigs.
    const build = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      if (width === 0 || height === 0) return;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.round(
        Math.max(46, Math.min(100, (width * height) / 17000)),
      );
      R = Math.min(width, height) * 0.52;
      pts = [];
      const dist = (a: CloudNode, b: CloudNode) =>
        Math.hypot(a.X - b.X, a.Y - b.Y, a.Z - b.Z, a.W - b.W);
      for (let n = 0; n < count; n++) {
        let best: CloudNode | null = null;
        let bestD = -1;
        for (let c = 0; c < 14; c++) {
          const u = Math.random() * 2 - 1;
          const v = Math.random() * 2 - 1;
          const w = Math.random() * 2 - 1;
          const q = Math.random() * 2 - 1;
          if (u * u + v * v + w * w > 1) {
            c--;
            continue;
          }
          const cand: CloudNode = {
            X: u * R * 1.25,
            Y: v * R * 0.75,
            Z: w * R * 0.55,
            W: q * R * 0.6,
            phase: 0,
            r: 0,
            accent: false,
            leaf: false,
          };
          let dMin = pts.length ? Infinity : 1;
          for (const p of pts) dMin = Math.min(dMin, dist(cand, p));
          if (dMin > bestD) {
            bestD = dMin;
            best = cand;
          }
        }
        const b = best!;
        b.phase = Math.random() * Math.PI * 2;
        b.r = Math.random() < 0.12 ? 2.8 : 1.9;
        b.accent = Math.random() < 0.14;
        pts.push(b);
      }

      edges = [];
      const keys = new Set<number>();
      const maxLen = R * 0.52;
      const link = (i: number, j: number): boolean => {
        const k = i < j ? i * 100000 + j : j * 100000 + i;
        if (keys.has(k)) return false;
        keys.add(k);
        edges.push([i, j]);
        return true;
      };
      pts.forEach((p, i) => {
        const near = pts
          .map((q, j) => ({ j, d: i === j ? Infinity : dist(p, q) }))
          .sort((a, b) => a.d - b.d)
          .slice(0, 3);
        let made = 0;
        for (const { j, d } of near) {
          if (d > maxLen || made >= 2 + (i % 2)) continue;
          if (link(i, j)) made++;
        }
      });

      // Leaves: short dead-end twigs off ~35% of the core nodes.
      const parents = pts.length;
      for (let i = 0; i < parents; i++) {
        if (Math.random() > 0.35) continue;
        const twigs = Math.random() < 0.3 ? 2 : 1;
        for (let s = 0; s < twigs; s++) {
          const p = pts[i];
          const dx = Math.random() * 2 - 1;
          const dy = Math.random() * 2 - 1;
          const dz = Math.random() * 2 - 1;
          const dl = Math.hypot(dx, dy, dz) || 1;
          const reach = R * (0.1 + Math.random() * 0.08);
          const leaf: CloudNode = {
            X: p.X + (dx / dl) * reach,
            Y: p.Y + (dy / dl) * reach,
            Z: p.Z + (dz / dl) * reach,
            W: p.W + (Math.random() * 2 - 1) * reach * 0.5,
            phase: Math.random() * Math.PI * 2,
            r: 1.25,
            accent: Math.random() < 0.08,
            leaf: true,
          };
          link(i, pts.push(leaf) - 1);
        }
      }

      proj = pts.map((p) => ({
        x: 0,
        y: 0,
        r: p.r,
        accent: p.accent,
        scale: 1,
        fog: 1,
        tint: null,
      }));
    };

    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      mouse.x = x;
      mouse.y = y;
      mouse.active = x >= 0 && y >= 0 && x <= rect.width && y <= rect.height;
    };

    const onScroll = () => {
      canvas.style.transform = `translate3d(0, ${window.scrollY * PARALLAX}px, 0)`;
    };

    // Vignette: 0.16 in the centre (quiet, behind text) → 1.0 toward the edges.
    const vignette = (x: number, y: number): number => {
      const nx = (x - width / 2) / (width / 2);
      const ny = (y - height / 2) / (height / 2);
      const dn = Math.sqrt(nx * nx + ny * ny);
      const t = Math.min(1, Math.max(0, (dn - 0.12) / 0.78));
      return 0.16 + 0.84 * t;
    };

    const offscreen = (n: ProjNode): boolean =>
      !Number.isFinite(n.x) ||
      !Number.isFinite(n.y) ||
      n.x < -240 ||
      n.x > width + 240 ||
      n.y < -240 ||
      n.y > height + 240;

    const frame = (t: number) => {
      // 3D orbit + 4D double rotation (X–W = the inside-out turn, plus Z–W).
      const ry = reduce ? 0.6 : t * YAW_SPEED;
      const rxA = 0.32 + (reduce ? 0 : Math.sin(t * 0.00005) * 0.07);
      const rw1 = reduce ? 0.8 : t * XW_SPEED;
      const rw2 = reduce ? 0.3 : t * ZW_SPEED;
      const cy = Math.cos(ry);
      const sy = Math.sin(ry);
      const cx = Math.cos(rxA);
      const sx = Math.sin(rxA);
      const cw1 = Math.cos(rw1);
      const sw1 = Math.sin(rw1);
      const cw2 = Math.cos(rw2);
      const sw2 = Math.sin(rw2);
      const f = Math.min(width, height) * 1.15;
      const f4 = R * 2.2;
      const mx = mouse.active ? (mouse.x - width / 2) / width : 0;
      const my = mouse.active ? (mouse.y - height / 2) / height : 0;

      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        const wob = reduce
          ? 0
          : Math.sin(t * 0.0004 + p.phase) * (p.leaf ? 9 : 6);
        let X = p.X + wob;
        let Y = p.Y;
        let Z = p.Z;
        let Wc = p.W;

        // 4D: rotate X–W then Z–W, then a CLAMPED 4D→3D perspective divide.
        const X4 = X * cw1 - Wc * sw1;
        const W4 = X * sw1 + Wc * cw1;
        const Z4 = Z * cw2 - W4 * sw2;
        const W5 = Z * sw2 + W4 * cw2;
        X = X4;
        Z = Z4;
        Wc = W5;
        const wT = Wc / (R * 0.6);
        const persp4 = Math.max(
          0.55,
          Math.min(1.9, f4 / Math.max(f4 * 0.4, f4 - Wc)),
        );
        X *= persp4;
        Y *= persp4;
        Z *= persp4;

        // 3D: yaw + pitch, then a clamped 3D→2D perspective divide.
        const X1 = X * cy + Z * sy;
        const Z1 = -X * sy + Z * cy;
        const Y1 = Y * cx - Z1 * sx;
        const Z2 = Y * sx + Z1 * cx;
        // Soft near-plane boundary: tanh-compress the approach toward the
        // camera so the cloud swells but never quite reaches the screen
        // (the hard floor stays as a backstop against any residual blowup).
        const NEAR = f * 0.4;
        const Zeff = Z2 < 0 ? -NEAR * Math.tanh(-Z2 / NEAR) : Z2;
        const persp = f / Math.max(f * 0.3, f + Zeff * 1.6);

        const o = proj[i];
        o.x = width / 2 + (X1 + mx * 40 * (Z2 / 100)) * persp;
        o.y = height / 2 + (Y1 + my * 40 * (Z2 / 100)) * persp;
        o.scale = Math.min(3, persp * persp4);
        const zn = Math.max(
          -1,
          Math.min(1, Z2 / (Math.min(width, height) * 0.4)),
        );
        o.fog = (0.35 + 0.65 * ((1 - zn) / 2)) * (p.leaf ? 0.75 : 1);
        o.tint = mix4(o.y / height, wT);
      }

      // ── render — low-alpha clear leaves motion trails (deep field) ──
      if (reduce) {
        ctx.clearRect(0, 0, width, height);
      } else {
        ctx.fillStyle = `rgba(5,7,10,${TRAIL_ALPHA})`;
        ctx.fillRect(0, 0, width, height);
      }
      for (const [i, j] of edges) {
        const a = proj[i];
        const b = proj[j];
        if (offscreen(a) && offscreen(b)) continue;
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        let alpha = 0.3 * vignette(midX, midY) * Math.min(a.fog, b.fog);
        if (mouse.active) {
          const dm = Math.hypot(mouse.x - midX, mouse.y - midY);
          if (dm < CURSOR_DIST) alpha += (1 - dm / CURSOR_DIST) * 0.55;
        }
        if (alpha < 0.012) continue;
        ctx.strokeStyle = `rgba(${a.tint ?? mix(midY / height)},${alpha})`;
        ctx.lineWidth = (0.7 * (a.scale + b.scale)) / 2;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      if (mouse.active) {
        for (const nd of proj) {
          const d = Math.hypot(mouse.x - nd.x, mouse.y - nd.y);
          if (d < CURSOR_DIST) {
            ctx.strokeStyle = `rgba(34,211,238,${(1 - d / CURSOR_DIST) * 0.85 * nd.fog})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(mouse.x, mouse.y);
            ctx.lineTo(nd.x, nd.y);
            ctx.stroke();
          }
        }
      }
      for (const nd of proj) {
        if (offscreen(nd)) continue;
        let prox = 0;
        if (mouse.active) {
          const d = Math.hypot(mouse.x - nd.x, mouse.y - nd.y);
          if (d < CURSOR_DIST) prox = 1 - d / CURSOR_DIST;
        }
        const r = nd.r * nd.scale;
        ctx.beginPath();
        if (prox > 0.12) {
          ctx.arc(nd.x, nd.y, r + prox * 2.6, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(56,224,250,0.98)";
          ctx.shadowColor = "rgba(34,211,238,0.95)";
          ctx.shadowBlur = 12 + prox * 22;
        } else {
          const vig = vignette(nd.x, nd.y) * nd.fog;
          const tint = nd.tint ?? mix(nd.y / height);
          ctx.arc(nd.x, nd.y, r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${tint},${(nd.accent ? 0.95 : 0.62) * vig})`;
          ctx.shadowColor = `rgba(${tint},${0.6 * vig})`;
          ctx.shadowBlur = (nd.accent ? 11 : 5) * vig;
        }
        ctx.fill();
      }
      ctx.shadowBlur = 0;
      if (mouse.active) {
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 3.4, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(56,224,250,1)";
        ctx.shadowColor = "rgba(34,211,238,1)";
        ctx.shadowBlur = 18;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    };

    const ro = new ResizeObserver(() => build());
    ro.observe(canvas);
    build();
    onScroll();

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    let raf = 0;
    if (reduce) {
      frame(1200);
    } else {
      const loop = (t: number) => {
        frame(t);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className}
      style={{
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        willChange: "transform",
      }}
    />
  );
}
